import {useEffect,useLayoutEffect,useRef,useState} from 'react';
import {AnnotationEditorType,AnnotationMode,getDocument,GlobalWorkerOptions} from 'pdfjs-dist';
import {EventBus,PDFLinkService,PDFViewer as ContinuousPDFViewer,LinkTarget} from 'pdfjs-dist/web/pdf_viewer.mjs';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {FileQuestion,LoaderCircle} from 'lucide-react';
import {errorMessage} from '../api';
import {clampPdfZoom,pdfAnchorOffset,pdfFitScale,pdfTouchGeometry,pdfWheelZoom,PDF_REPAINT_DELAY} from '../pdf-viewport';
import {pdfDocumentOptions} from '../pdf-decoders';
import {PdfInkAdapter} from '../pdf-ink-adapter';
import {PdfInkSession} from '../pdf-ink-session';
import {registerPdfCapture} from '../capture-reader';
import {annotatedPdfName,downloadPdfInk,verifyPdfInkSource} from '../pdf-ink-download';
import {usePdfInkStyle} from '../pdf-ink-preferences';
import {PdfInkToolbar,type PdfInkActions,type PdfInkTool} from './PdfInkToolbar';
import '../pdf-reader.css';

GlobalWorkerOptions.workerSrc=workerUrl;

export interface PdfViewerProps{
 url:string;
 page:number;
 /** Changes for an explicit jump, including a jump to the current page. */
 navigationKey?:number;
 onPage:(page:number)=>void;
 onPages:(pages:number)=>void;
 zoom:number;
 onZoom:(zoom:number)=>void;
 labels?:Array<{pageNumber:number;label:string}>;
 onSelection:(text:string)=>void;
 inkIdentity?:{userId:string;fileId:string;contentRevision:string;name:string};
 inkEnabled?:boolean;
 onInkEnabled?:(enabled:boolean)=>void;
 onInkActions?:(actions:PdfInkActions|null)=>void;
}
type InkStatus={status:'loading'|'saved'|'saving'|'pending'|'conflict'|'error';message?:string};
interface ReaderRuntime{
 viewer:ContinuousPDFViewer;
 fitScale:number;
 ready:boolean;
 zoom:number;
 requestedPage:number;
 applyZoom:(zoom:number,origin?:{x:number;y:number})=>void;
 ink:PdfInkAdapter|null;
 inkSession:PdfInkSession|null;
 inkLoaded:boolean;
 loadInk?:()=>Promise<void>;
}

/** PDF.js owns page layout, visible-first rendering, text/annotation layers and canvas eviction. */
export function PdfViewer(props:PdfViewerProps){
 const latest=useRef(props);latest.current=props;
 const container=useRef<HTMLDivElement>(null),pagesHost=useRef<HTMLDivElement>(null);
 const runtime=useRef<ReaderRuntime|null>(null);
 const [error,setError]=useState(''),[loading,setLoading]=useState(true),[ready,setReady]=useState(false),[currentPage,setCurrentPage]=useState(props.page),[hudVisible,setHudVisible]=useState(true);
 const hudTimer=useRef(0),lastNavigation=useRef(props.navigationKey);
 const [inkReady,setInkReady]=useState(false),[inkStatus,setInkStatus]=useState<InkStatus>({status:'loading'}),[inkTool,setInkTool]=useState<PdfInkTool>('draw'),[inkEditorState,setInkEditorState]=useState({canUndo:false,canRedo:false,hasSelection:false}),[exporting,setExporting]=useState(false);
 const [inkRecoveryAvailable,setInkRecoveryAvailable]=useState(false),[documentRetry,setDocumentRetry]=useState(0);
 const {color:inkColor,thickness:inkThickness,setColor:setInkColor,setThickness:setInkThickness}=usePdfInkStyle(props.inkIdentity?.userId);
 const inkSettings=useRef({tool:inkTool,color:inkColor,thickness:inkThickness});inkSettings.current={tool:inkTool,color:inkColor,thickness:inkThickness};
 const canWrite=inkReady&&inkStatus.status!=='conflict'&&inkStatus.status!=='error';
 function showHUD(){setHudVisible(true);window.clearTimeout(hudTimer.current);hudTimer.current=window.setTimeout(()=>setHudVisible(false),2500);}
 useEffect(()=>()=>window.clearTimeout(hudTimer.current),[]);

 useEffect(()=>{
  const element=container.current,host=pagesHost.current;if(!element||!host)return;
  const abort=new AbortController();let disposed=false,initializedPages=false,initialVisibleReady=element.clientWidth>0,suppressPageEvents=false,firstPageWidth=0,measuredWidth=0,frame=0;
  let touchPinch:{distance:number;zoom:number;x:number;y:number}|null=null,gestureZoom:number|null=null,lastTouchPinch=-Infinity;
  const retainedRasters=new Map<number,HTMLCanvasElement>();let unregisterCapture:(()=>void)|undefined;
  setError('');setLoading(true);setReady(false);setCurrentPage(latest.current.page);lastNavigation.current=latest.current.navigationKey;
  setInkReady(false);setInkRecoveryAvailable(false);setInkStatus({status:'loading'});setInkEditorState({canUndo:false,canRedo:false,hasSelection:false});latest.current.onInkActions?.(null);
  const eventBus=new EventBus();
  const linkService=new PDFLinkService({eventBus,externalLinkTarget:LinkTarget.BLANK,externalLinkRel:'noopener noreferrer',ignoreDestinationZoom:true});
  // Runtime supports abortSignal even though pdfjs-dist's generated options type omits it.
  const options={container:element,viewer:host,eventBus,linkService,annotationMode:AnnotationMode.ENABLE,annotationEditorMode:AnnotationEditorType.NONE,enableAutoLinking:false,enableDetailCanvas:false,maxCanvasPixels:8_388_608,maxCanvasDim:8192,minDurationToUpdateCanvas:PDF_REPAINT_DELAY,abortSignal:abort.signal};
  const viewer=new ContinuousPDFViewer(options);linkService.setViewer(viewer);
  const reportPage=()=>{
   if(disposed||!initializedPages||!initialVisibleReady||element.clientWidth<=0||suppressPageEvents||!state.ready)return;
   const number=viewer.currentPageNumber;
   host.querySelector('[data-current-page="true"]')?.removeAttribute('data-current-page');
   viewer.getPageView(number-1)?.div.setAttribute('data-current-page','true');
   element.dataset.currentPage=String(number);setCurrentPage(number);
   if(number!==latest.current.page)latest.current.onPage(number);
  };
  const releaseRaster=(pageNumber:number)=>{
   const retained=retainedRasters.get(pageNumber);if(!retained)return;
   retained.remove();retained.width=retained.height=0;retainedRasters.delete(pageNumber);
  };
  const trimRetainedRasters=()=>{
   if(suppressPageEvents)return;
   const viewport=element.getBoundingClientRect();
   for(const [number,canvas] of retainedRasters){
    const rect=canvas.getBoundingClientRect();
    if(!canvas.isConnected||rect.bottom<=viewport.top||rect.top>=viewport.bottom)releaseRaster(number);
   }
  };
  const preserveRasters=()=>{
   trimRetainedRasters();const viewport=element.getBoundingClientRect(),added:number[]=[];
   // This snapshots only the cached pages currently on screen, never the whole book.
   for(const pageView of viewer.getCachedPageViews()){
    const page=pageView.div as HTMLDivElement,rect=page.getBoundingClientRect();
    if(rect.bottom<=viewport.top||rect.top>=viewport.bottom||retainedRasters.has(pageView.id))continue;
    const wrapper=page.querySelector<HTMLDivElement>('.canvasWrapper');
    const source=wrapper?.querySelector<HTMLCanvasElement>('canvas:not(.pdf-retained-raster)');
    if(!wrapper||!source||source.width<=0||source.height<=0)continue;
    const retained=document.createElement('canvas');retained.className='pdf-retained-raster';retained.setAttribute('aria-hidden','true');
    retained.width=source.width;retained.height=source.height;retained.getContext('2d')?.drawImage(source,0,0);
    // PDF.js keeps ink SVGs in this wrapper above its raster. Insert the
    // temporary raster beside the source so it cannot cover those live SVGs.
    source.after(retained);retainedRasters.set(pageView.id,retained);added.push(pageView.id);
   }
   return added;
  };
  const state:ReaderRuntime={viewer,ink:null,inkSession:null,inkLoaded:false,fitScale:1,ready:false,zoom:clampPdfZoom(latest.current.zoom),requestedPage:Math.max(1,latest.current.page),applyZoom:(value,origin)=>{
   if(!state.ready)return;
   const zoom=clampPdfZoom(value);state.zoom=zoom;element.dataset.zoom=String(zoom);
   if(!initialVisibleReady||element.clientWidth<=0)return;
   const targetScale=state.fitScale*zoom;
   if(Math.abs(targetScale-viewer.currentScale)<.0001)return;
   const rect=element.getBoundingClientRect(),point=origin??{x:rect.width/2,y:rect.height/2};
   const hit=document.elementFromPoint(rect.left+point.x,rect.top+point.y)?.closest<HTMLElement>('.pdf-page');
   const anchorPage=hit&&host.contains(hit)?hit:viewer.getPageView(viewer.currentPageNumber-1)?.div;
   const before=anchorPage?.getBoundingClientRect();
   const anchor=before&&before.width&&before.height?{x:(rect.left+point.x-before.left)/before.width,y:(rect.top+point.y-before.top)/before.height}:null;
   const previousScale=viewer.currentScale,addedRasters=preserveRasters();suppressPageEvents=true;
   // Keep a presentation copy because upstream cancellation can remove both render canvases.
   viewer.updateScale({scaleFactor:targetScale/previousScale,drawingDelay:PDF_REPAINT_DELAY});
   if(viewer.currentScale===previousScale)addedRasters.forEach(releaseRaster);
   if(anchorPage&&anchor){
    const after=anchorPage.getBoundingClientRect();
    element.scrollLeft=pdfAnchorOffset(after.left-rect.left+element.scrollLeft,after.width,anchor.x,point.x);
    element.scrollTop=pdfAnchorOffset(after.top-rect.top+element.scrollTop,after.height,anchor.y,point.y);
   }
   viewer.update();suppressPageEvents=false;reportPage();
  }};
  runtime.current=state;
  const resize=()=>{
   const width=element.clientWidth;if(width<=0||!firstPageWidth||!state.ready)return;
   const wasInitiallyHidden=!initialVisibleReady;
   if(Math.abs(width-measuredWidth)<.5&&!wasInitiallyHidden){viewer.update();return;}
   measuredWidth=width;state.fitScale=pdfFitScale(width,firstPageWidth);
   if(wasInitiallyHidden){
    // Defer scale/navigation until layout exists; PDF.js cannot scroll a display:none page.
    viewer.currentScale=state.fitScale*state.zoom;
    viewer.currentPageNumber=Math.max(1,Math.min(viewer.pagesCount,state.requestedPage));
    initialVisibleReady=true;reportPage();
   }else state.applyZoom(state.zoom);
  };
  const resizeObserver=new ResizeObserver(()=>{cancelAnimationFrame(frame);frame=requestAnimationFrame(resize);});resizeObserver.observe(element);
  const initialized=()=>{
   if(disposed)return;
   for(let index=0;index<viewer.pagesCount;index++){
    const page=viewer.getPageView(index).div;page.classList.add('pdf-page');page.setAttribute('aria-label',`PDF 第 ${index+1} 页`);
   }
   initialVisibleReady=element.clientWidth>0;
   state.zoom=clampPdfZoom(latest.current.zoom);
   state.fitScale=pdfFitScale(element.clientWidth||648,firstPageWidth);measuredWidth=element.clientWidth;
   // The public getter reports 1 before initialization, while updateScale still multiplies its internal zero.
   if(initialVisibleReady)viewer.currentScale=state.fitScale*state.zoom;
   state.ready=true;element.dataset.zoom=String(state.zoom);
   state.requestedPage=Math.max(1,latest.current.page);
   if(initialVisibleReady)viewer.currentPageNumber=Math.max(1,Math.min(viewer.pagesCount,state.requestedPage));
   lastNavigation.current=latest.current.navigationKey;initializedPages=true;
   reportPage();setReady(true);showHUD();
  };
  const rendered=({error:renderError,pageNumber,cssTransform}:{error?:unknown;pageNumber:number;cssTransform?:boolean})=>{
   if(disposed)return;
   if(renderError){setError(errorMessage(renderError));return;}
   // A CSS-only notification does not prove a replacement raster exists.
   if(!cssTransform)releaseRaster(pageNumber);
   setLoading(false);
  };
  eventBus.on('pagesinit',initialized);eventBus.on('pagechanging',reportPage);eventBus.on('pagerendered',rendered);eventBus.on('updateviewarea',trimRetainedRasters);
  const identity=latest.current.inkIdentity;
  const inkError=(value:unknown)=>{if(!disposed)setInkStatus({status:'error',message:errorMessage(value)});};
  if(identity){
   const session=new PdfInkSession({...identity,onStatus:value=>{if(!disposed){setInkStatus(value);setInkRecoveryAvailable(state.inkSession?.hasRecovery??false);}},onRemotePage:index=>{
    const adapter=state.ink;if(!adapter||disposed)return;
    void adapter.commit().then(()=>adapter.hydratePage(index,state.inkSession?.annotations.filter(annotation=>annotation.pageIndex===index)??[])).catch(inkError);
   }});
   state.inkSession=session;
   // Local recovery must remain available even if the source PDF cannot be fetched.
   void session.loadRecovery().then(()=>{if(!disposed)setInkRecoveryAvailable(session.hasRecovery);}).catch(inkError);
  }
  const task=getDocument(pdfDocumentOptions(props.url));
  void task.promise.then(async document=>{
   const firstPage=await document.getPage(1);if(disposed)return;
   firstPageWidth=firstPage.getViewport({scale:1}).width;
   const adapter=new PdfInkAdapter({viewer,document,scrollElement:element,onChange:(index,annotations)=>state.inkSession?.pageChanged(index,annotations),onState:value=>{if(!disposed)setInkEditorState(value);},onError:inkError});
   state.ink=adapter;
   unregisterCapture=registerPdfCapture(element,async()=>{
    if(disposed||!state.ready||(identity&&!state.inkLoaded))throw new Error('PDF 和批注仍在加载，请稍后截图。');
    await adapter.commit();if(state.inkSession)await adapter.hydrate(state.inkSession.annotations);
    return adapter.capturePage(viewer.currentPageNumber-1);
   });
   latest.current.onPages(document.numPages);linkService.setDocument(document);viewer.setDocument(document);
   if(identity&&state.inkSession){
    const session=state.inkSession;
    state.loadInk=async()=>{
     await verifyPdfInkSource(await document.getData(),identity.contentRevision);if(disposed)return;
     await session.load();if(disposed)return;
     await adapter.hydrate(session.annotations);if(disposed)return;
     state.inkLoaded=true;setInkReady(true);adapter.setColor(inkSettings.current.color);adapter.setThickness(inkSettings.current.thickness);
    };
    void state.loadInk().catch(inkError);
   }
  }).catch(value=>{if(!disposed){setError(errorMessage(value));setLoading(false);if(identity)inkError(new Error('PDF 未能加载，无法确认原文件版本，暂时不能书写。'));}});

  const applyGesture=(zoom:number,clientX:number,clientY:number)=>{
   if(!state.ready)return;const rect=element.getBoundingClientRect();const next=clampPdfZoom(zoom);
   state.applyZoom(next,{x:clientX-rect.left,y:clientY-rect.top});latest.current.onZoom(next);showHUD();
  };
  const wheel=(event:WheelEvent)=>{
   if(!event.ctrlKey||!state.ready)return;event.preventDefault();if(gestureZoom!==null||touchPinch)return;
   applyGesture(pdfWheelZoom(state.zoom,event.deltaY,event.deltaMode),event.clientX,event.clientY);
  };
  const touchStart=(event:TouchEvent)=>{
   const geometry=pdfTouchGeometry(event.touches);if(!geometry||!state.ready)return;
   event.preventDefault();lastTouchPinch=performance.now();gestureZoom=null;
   touchPinch={distance:Math.max(1,geometry.distance),zoom:state.zoom,x:geometry.x,y:geometry.y};
  };
  const touchMove=(event:TouchEvent)=>{
   const geometry=pdfTouchGeometry(event.touches);if(!geometry||!touchPinch)return;
   event.preventDefault();lastTouchPinch=performance.now();
   applyGesture(touchPinch.zoom*geometry.distance/touchPinch.distance,touchPinch.x,touchPinch.y);
   element.scrollLeft-=geometry.x-touchPinch.x;element.scrollTop-=geometry.y-touchPinch.y;
   touchPinch.x=geometry.x;touchPinch.y=geometry.y;viewer.update();reportPage();
  };
  const touchEnd=(event:TouchEvent)=>{if(event.touches.length<2)touchPinch=null;};
  type SafariGesture=Event&{scale:number;clientX:number;clientY:number};
  const gestureStart=(raw:Event)=>{if(!state.ready)return;raw.preventDefault();if(!touchPinch&&performance.now()-lastTouchPinch>300)gestureZoom=state.zoom;};
  const gestureChange=(raw:Event)=>{
   if(!state.ready)return;raw.preventDefault();if(gestureZoom===null||touchPinch)return;
   const event=raw as SafariGesture;applyGesture(gestureZoom*event.scale,event.clientX,event.clientY);
  };
  const gestureEnd=()=>{gestureZoom=null;};
  element.addEventListener('wheel',wheel,{passive:false});element.addEventListener('touchstart',touchStart,{passive:false});element.addEventListener('touchmove',touchMove,{passive:false});element.addEventListener('touchend',touchEnd);element.addEventListener('touchcancel',touchEnd);
  element.addEventListener('gesturestart',gestureStart,{passive:false});element.addEventListener('gesturechange',gestureChange,{passive:false});element.addEventListener('gestureend',gestureEnd);
  return()=>{
   disposed=true;runtime.current=null;cancelAnimationFrame(frame);resizeObserver.disconnect();abort.abort();unregisterCapture?.();
   state.ink?.destroy();state.inkSession?.destroy();latest.current.onInkActions?.(null);
   eventBus.off('pagesinit',initialized);eventBus.off('pagechanging',reportPage);eventBus.off('pagerendered',rendered);eventBus.off('updateviewarea',trimRetainedRasters);
   element.removeEventListener('wheel',wheel);element.removeEventListener('touchstart',touchStart);element.removeEventListener('touchmove',touchMove);element.removeEventListener('touchend',touchEnd);element.removeEventListener('touchcancel',touchEnd);
   element.removeEventListener('gesturestart',gestureStart);element.removeEventListener('gesturechange',gestureChange);element.removeEventListener('gestureend',gestureEnd);
   // setDocument(null) is the upstream reset API; generated types omit its nullable input.
   retainedRasters.forEach(retained=>retained.remove());retainedRasters.clear();
   viewer.setDocument(null!);linkService.setDocument(null);void task.destroy();
  };
 },[props.url,props.inkIdentity?.userId,props.inkIdentity?.fileId,props.inkIdentity?.contentRevision,documentRetry]);

 async function exportInk(){
  const state=runtime.current,identity=latest.current.inkIdentity;
  if(!state?.inkLoaded||!state.ink||!state.inkSession||!identity)throw new Error('批注尚未读取完成，请稍后重试。');
  setExporting(true);
  try{
   await state.ink.commit();await state.ink.hydrate(state.inkSession.annotations);
   const bytes=await state.ink.exportPdf();
   downloadPdfInk(new Blob([new Uint8Array(bytes)],{type:'application/pdf'}),annotatedPdfName(identity.name));
  }finally{setExporting(false);}
 }
 useEffect(()=>{latest.current.onInkActions?.({ready:inkReady,exporting,exportPdf:exportInk});},[inkReady,exporting]);
 useEffect(()=>{
  const adapter=runtime.current?.ink;if(!adapter)return;
  void adapter.setMode(props.inkEnabled&&canWrite?inkTool:'read').catch(value=>setInkStatus({status:'error',message:errorMessage(value)}));
 },[props.inkEnabled,canWrite,inkTool]);
 useEffect(()=>{runtime.current?.ink?.setColor(inkColor);},[inkColor]);
 useEffect(()=>{runtime.current?.ink?.setThickness(inkThickness);},[inkThickness]);
 async function retryInk(){
  const state=runtime.current;if(!state?.inkSession)return;
  if(!state.ink){setDocumentRetry(value=>value+1);return;}
  try{
   if(!state.inkLoaded){await state.loadInk?.();return;}
   await state.ink.commit();
   // The session distinguishes offline drafts, version conflicts and local-storage errors.
   // Do not turn its pending/offline status into a disabled editor in this catch.
   try{await state.inkSession.retry();}catch{return;}
   await state.ink.hydrate(state.inkSession.annotations);
  }catch(value){setInkStatus({status:'error',message:errorMessage(value)});}
 }
 async function useCloudInk(){
  const state=runtime.current;if(!state?.inkSession||!state.ink)return;
  if(!window.confirm('使用云端版本？本地未同步笔迹将移出当前页面，可先下载恢复数据。'))return;
  try{await state.ink.commit();await state.inkSession.useServerVersion();await state.ink.hydrate(state.inkSession.annotations);}
  catch(value){setInkStatus({status:'error',message:errorMessage(value)});}
 }
 function downloadRecovery(){
  const session=runtime.current?.inkSession;if(!session)return;
  downloadPdfInk(new Blob([session.recoveryData],{type:'application/json'}),`${latest.current.inkIdentity?.name.replace(/\.pdf$/i,'')||'PDF'}-批注恢复数据.json`);
 }

 useEffect(()=>{
  const state=runtime.current;if(!ready||!state?.ready)return;
  const explicit=lastNavigation.current!==props.navigationKey;lastNavigation.current=props.navigationKey;
  const page=Math.max(1,Math.min(state.viewer.pagesCount,props.page));
  state.requestedPage=page;
  if(!container.current?.clientWidth)return;
  if(explicit||state.viewer.currentPageNumber!==page){state.viewer.currentPageNumber=page;showHUD();}
 },[ready,props.page,props.navigationKey]);
 // Apply controlled changes in the commit itself. A passive effect can run after
 // the next native wheel event and rewind its newer accumulated gesture zoom.
 useLayoutEffect(()=>{const state=runtime.current;if(ready&&state?.ready&&Math.abs(state.zoom-clampPdfZoom(props.zoom))>.00001)state.applyZoom(props.zoom);},[ready,props.zoom]);
 useEffect(()=>{
  const state=runtime.current;if(!ready||!state?.ready)return;
  const configured=new Map(props.labels?.map(label=>[label.pageNumber,label.label]));
  const labels=Array.from({length:state.viewer.pagesCount},(_,index)=>configured.get(index+1)??String(index+1));
  state.viewer.setPageLabels(labels);
 },[ready,props.labels]);
 const selection=()=>{
  if(latest.current.inkEnabled)return;
  const value=window.getSelection();if(value&&container.current?.contains(value.anchorNode)&&container.current.contains(value.focusNode)&&value.toString().trim())latest.current.onSelection(value.toString().slice(0,12000));
 };
 const inkStatusText=inkStatus.message||({loading:'正在读取批注…',saved:'已保存',saving:'正在保存批注…',pending:'批注保留在此设备，等待同步',conflict:'批注存在其他版本，本地笔迹已保留。',error:'批注暂时不可用，请重试。'} satisfies Record<InkStatus['status'],string>)[inkStatus.status];
 return <div className={`pdf-reader-shell${props.inkEnabled&&props.inkIdentity?' pdf-ink-enabled':''}`}>
  <div className="pdf-scroll" ref={container} role="region" aria-label="PDF 连续阅读区" tabIndex={0} data-current-page={currentPage} data-zoom={clampPdfZoom(props.zoom)} onScroll={showHUD} onMouseUp={selection} onTouchEnd={selection}><div className="pdfViewer reados-pdf-pages" ref={pagesHost}/></div>
  {loading&&!error&&<div className="pdf-initial-loading" role="status"><LoaderCircle size={22} className="spin"/><span>正在加载 PDF</span></div>}
  {error&&<div className="pdf-document-error preview-error" role="alert"><FileQuestion size={28}/><strong>暂时无法呈现这份 PDF</strong><p>{error}</p><a className="secondary" href={`${props.url}${props.url.includes('?')?'&':'?'}download=1`} download>下载原文件</a></div>}
  {ready&&hudVisible&&!error&&<div className="pdf-page-hud" aria-live="off">{props.labels?.find(item=>item.pageNumber===currentPage)?.label||currentPage}</div>}
  {props.inkIdentity&&props.inkEnabled&&<PdfInkToolbar tool={inkTool} onTool={setInkTool} color={inkColor} onColor={setInkColor} thickness={inkThickness} onThickness={setInkThickness} {...inkEditorState} disabled={!canWrite} onUndo={()=>runtime.current?.ink?.undo()} onRedo={()=>runtime.current?.ink?.redo()} onDelete={()=>runtime.current?.ink?.deleteSelection()} onDone={()=>props.onInkEnabled?.(false)}/>}
  {props.inkIdentity&&(props.inkEnabled||(inkRecoveryAvailable&&!inkReady)||['pending','conflict','error'].includes(inkStatus.status))&&<div className={`pdf-ink-status pdf-ink-status-${inkStatus.status}`} role="status" aria-live="polite"><span>{inkStatusText}</span>
   {['pending','error'].includes(inkStatus.status)&&<button type="button" onClick={()=>void retryInk()}>重试</button>}
   {((inkRecoveryAvailable&&!inkReady)||(['pending','error','conflict'].includes(inkStatus.status)&&(inkReady||inkRecoveryAvailable)))&&<button type="button" onClick={downloadRecovery}>下载恢复数据</button>}
   {inkStatus.status==='conflict'&&<button type="button" onClick={()=>void useCloudInk()}>使用云端版本</button>}
  </div>}
 </div>;
}
